import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Accessible modal wrapper (Phase 1 UX sweep, venue+admin rows 62/63).
 *
 * Hand-rolled overlays across the admin portal had no `role="dialog"`, no
 * Escape handler and no focus management, so Tab walked straight out of the
 * dialog and into the page behind it. This wrapper gives every one of them:
 *
 *   - `role="dialog" aria-modal="true"` labelled by its own heading
 *   - Escape closes, and a click on the backdrop closes
 *   - initial focus on the heading, focus restored to the opener on close
 *   - Tab / Shift+Tab cycle inside the panel
 *   - `max-h-[90vh] overflow-y-auto` so a tall dialog scrolls on a short phone
 *     instead of pushing its confirm buttons off screen
 *
 * Usage:
 *   <Modal isOpen={open} onClose={close} title="Change plan" maxWidth="max-w-md">
 *     …body and action buttons…
 *   </Modal>
 *
 * `title` may be a node when a heading needs more than text; pass `titleText`
 * as well in that case so the accessible name stays useful.
 */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-md',
  panelClassName = '',
  headingClassName = 'font-display text-xl text-navy',
  showClose = true,
  closeLabel = 'Close',
  closeOnBackdrop = true,
}) {
  const panelRef = useRef(null);
  const headingRef = useRef(null);
  const openerRef = useRef(null);
  const headingId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;
    openerRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    headingRef.current?.focus?.();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === headingRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      openerRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={closeOnBackdrop ? (e) => { if (e.target === e.currentTarget) onClose?.(); } : undefined}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className={`relative w-full ${maxWidth} max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl p-5 sm:p-6 ${panelClassName}`}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 id={headingId} ref={headingRef} tabIndex={-1} className={`${headingClassName} outline-none`}>
            {title}
          </h2>
          {showClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="shrink-0 p-1 rounded-lg text-navy/50 hover:text-navy hover:bg-navy/5 transition-colors"
            >
              <X size={20} aria-hidden="true" />
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
